import {
  Recurrence, RecurrenceDetails, SelfServiceSchedulePaneProps, StaticInitializers, TimeZoneDetails
} from '@tableau/self-service-schedules';
import {
  DailyRecurrenceDetails, HourlyRecurrenceDetails, MonthlyRecurrenceDetails, RecurrenceMode,
  RecurrenceType, WeeklyRecurrenceDetails
} from '@tableau/self-service-schedules/lib/src/components/Recurrence';
import {
  SelfServiceSchedulePaneType
} from '@tableau/self-service-schedules/lib/src/components/SelfServiceSchedulePane';
import { SiteUrls } from '@tableau/shared-urls';

import {
  ActionHelpers, standardActionErrorMsg, standardActionSuccessMsg, StandardActionToastMessages
} from '~commonInfra/contentActions/ActionHelpers';
import { IErrorDetail } from '~commonInfra/contentActions/ActionMessages';
import { IErrorItem } from '~commonInfra/contentActions/MultipleItemResult';
import { openReactActionDialog } from '~commonInfra/dialogs/ReactActionDialog';
import { TabDialogWidth } from '~commonInfra/dialogs/style/TabDialogWidth';
import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import {
  CommonErrorCode, SchedulingErrorCode, SubscriptionErrorCode
} from '~commonInfra/utils/ErrorCodes';
import { Set } from '~commonInfra/utils/Set';
import { LinkedTasks } from '~src/linkedTasks/resources/LinkedTasks';
import { areLinkedTasksEnabled } from '~src/linkedTasks/utils/LinkedTaskHelpers';
import { SubscriptionSchedulePickerActionBody, SubscriptionSchedulePickerProps } from '~src/schedule/components/SubscriptionSchedulePickerActionBody';

import { SessionServerSelectors } from '../../bootstrap/redux/selectors/SessionInfoSelectors';
import { getAppStore } from '../../bootstrap/redux/store/AppStore';
import { Messages } from '../../codegen/Localize';
import { L10n } from '../../commonInfra/L10n/L10n';
import { IExtractTaskInfo } from '../../extractTask/models/IExtractTaskInfo';
import { ExtractTasks } from '../../extractTask/resources/ExtractTasks';
import { RunFlowTaskInfo } from '../../flow/models/RunFlowTaskInfo';
import { RunFlowTasks } from '../../flow/resources/RunFlowTasks';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import {
  getSessionManagementServiceInstance
} from '../../sessionManagement/SessionManagementService';
import { ScheduleContentType } from '../../spacesPlatform/models/ContentTypes';
import { INamedItemInfo, IScheduledItemInfo } from '../../spacesPlatform/models/resourceInfoParts';
import { ISubscriptionInfo, SubscriptionTargetTypes } from '../../subscription/models/ISubscriptionInfo';
import { Subscriptions } from '../../subscription/models/Subscriptions';
import {
  SchedulePickerActionBody, SchedulePickerProps, SchedulePickerState, SubscriptionFrequency
} from '../components/SchedulePickerActionBody';
import {
  SelfServiceScheduleSubscriptionDialogBody, SelfServiceScheduleSubscriptionDialogState
} from '../components/SelfServiceScheduleSubscriptionDialog';
import { SchedulesService } from '../resources/Schedules';
import { ScheduleDetails } from '../serverApi/types/ScheduleDetails';
import {
  scheduledActionEnum, scheduledTypeEnum, SelfServiceSchedule
} from '../serverApi/types/SelfServiceSchedule';
import {
  DefinedBy, ISchedule, ISelfServiceScheduleDetails, ScheduleActions
} from '../types/ISchedule';

interface IChangeScheduleActionOptions<T> {
  notificationTranslationFns: StandardActionToastMessages;
  scheduledActionType: string;
  descriptionTranslateKey: string;
  descriptionTranslateValues: _.Dictionary<any>;
}

interface IChangeAdminScheduleActionOptions<T> extends IChangeScheduleActionOptions<T> {
  errorDetailFn: (schedule: ISchedule) => (errorItem: IErrorItem<T>) => IErrorDetail;
  action: (ids: string[], scheduleId: string) => Promise<IResult>;
}
interface IChangeLinkedTasksActionOptions<T> extends IChangeScheduleActionOptions<T> {
  errorDetailFn: (schedule: ISchedule) => (errorItem: IErrorItem<T>) => IErrorDetail;
  action: (linkedTaskIds: string[], flowTaskIds: string[], scheduleId: string) => Promise<IResult>;
}

interface IChangeSubscriptionsFrequencyOptions<T> extends IChangeScheduleActionOptions<T> {
  errorDetailFn: (schedule: ISchedule) => (errorItem: IErrorItem<ISubscriptionInfo>) => IErrorDetail;
  action: (ids: string[], scheduleId: string, subscribeOnExtractRefresh: boolean) =>
    Promise<IResult>;
}

interface IChangeSelfServiceSubscriptionsFrequencyOptions<T> extends IChangeScheduleActionOptions<T> {
  errorDetailFn: () => (errorItem: IErrorItem<ISubscriptionInfo>) => IErrorDetail;
  action: (ids: string[], schedule: SelfServiceSchedule, subscribeOnExtractRefresh: boolean) =>
    Promise<IResult>;
  frequncyOption?: SubscriptionFrequency;
}

interface IChangeSelfServiceScheduleActionOptions<T> extends IChangeScheduleActionOptions<T> {
  errorDetailFn: () => (errorItem: IErrorItem<T>) => IErrorDetail;
  action: (ids: string[], schedule: SelfServiceSchedule) => Promise<IResult>;
}

export enum SubscriptionsFrequency {
  subscribeOnSelectedSchedule = 1 << 0,
  subscribeOnExtractRefresh = 1 << 1
}

export namespace ChangeScheduleActionService {
  // Generic logic
  export function getSelfServiceSchedulePaneProps(scheduleDetails: ISelfServiceScheduleDetails,
    timeZonesEnabled: boolean, timeZoneDisplayName: string, recurrenceType: string,
    type: SelfServiceSchedulePaneType): SelfServiceSchedulePaneProps {
    const details = createRecurrence(scheduleDetails, recurrenceType);
    const recurrence = {
      recurrenceMode: RecurrenceMode.TimeRecurrence,
      recurrenceType: recurrenceType,
      recurrenceDetails: details
    } as Recurrence;

    const sessionManagementService = getSessionManagementServiceInstance();
    const userAccountSettingsLink = new SiteUrls({
      siteUrlName: sessionManagementService.sessionSiteUrlName(),
      queryString: window.location.search
    }).userSettingsUrl(sessionManagementService.sessionUserInfo().username, sessionManagementService.sessionUserInfo().domainName);

    const timeZoneDetails: TimeZoneDetails = {
      timeZonesEnabled: timeZonesEnabled,
      timeZoneDisplayName: timeZoneDisplayName,
      linkToUserSettings: userAccountSettingsLink
    };

    const selfServiceSchedulePaneProps: SelfServiceSchedulePaneProps = {
      recurrenceModel: recurrence,
      timeZoneDetails: timeZoneDetails,
      type: type,
      recurrenceModelChangedCallback: (model) => { }
    };

    return selfServiceSchedulePaneProps;
  }

  function createRecurrence(scheduleDetails: ISelfServiceScheduleDetails, recurrenceType: string): RecurrenceDetails {
    switch (recurrenceType) {
      case RecurrenceType.Hourly:
        return {
          startTime: scheduleDetails.startTime,
          endTime: scheduleDetails.endTime,
          recurrenceMinutes: scheduleDetails.recurrenceMinutes,
          recurrenceDaysOfWeek: scheduleDetails.recurrenceDaysOfWeek
        } as HourlyRecurrenceDetails;
      case RecurrenceType.Daily:
        return {
          startTime: scheduleDetails.startTime,
          endTime: scheduleDetails.endTime,
          recurrenceMinutes: scheduleDetails.recurrenceMinutes,
          recurrenceDaysOfWeek: scheduleDetails.recurrenceDaysOfWeek
        } as DailyRecurrenceDetails;
      case RecurrenceType.Weekly:
        return {
          startTime: scheduleDetails.startTime,
          recurrenceDaysOfWeek: scheduleDetails.recurrenceDaysOfWeek
        } as WeeklyRecurrenceDetails;
      case RecurrenceType.Monthly:
        return {
          startTime: scheduleDetails.startTime,
          recurrenceDayOfMonth: scheduleDetails.recurrenceDayOfMonth,
          recurrenceDaysOfWeek: scheduleDetails.recurrenceDaysOfWeek
        } as MonthlyRecurrenceDetails;
      default:
        return StaticInitializers.defaultHourly();
    }
  }

  function mixedScheduleTypesUnsupported(): Promise<IResult> {
    ToasterService.instance.pop({ type: ToastType.Error, text: Messages.mixedScheduleTypesUnsupported() });
    return null;
  }

  function executeSelfServiceSchedule<T extends IScheduledItemInfo>(
    items: Set<T>,
    options: IChangeSelfServiceSubscriptionsFrequencyOptions<T>
  ): Promise<IResult> {
    const schedule = items.first().schedule;
    const timeZonesEnabled = FeatureFlags.userTimeZoneEnabled;
    const timeZoneDisplayName = getSessionManagementServiceInstance().userTimeZone().displayName;
    const sssProps = getSelfServiceSchedulePaneProps(schedule.scheduleDetails, timeZonesEnabled, timeZoneDisplayName, schedule.scheduleType, SelfServiceSchedulePaneType.Default);
    const initialDialogState = {
      recurrence: sssProps.recurrenceModel,
      selectedSubscriptionFrequency: options.frequncyOption,
    };
    const modalInstance = openReactActionDialog({
      title: Messages.changeFrequency(),
      bodyComponent: SelfServiceScheduleSubscriptionDialogBody,
      initialBodyProps: sssProps,
      initialDialogState,
      actionName: Messages.done(),
      dialogWidth: TabDialogWidth.width500px,
      dismissDialogOnActionFailed: true,
      action: (bodyProps: {}, state: SelfServiceScheduleSubscriptionDialogState) => {
        const schedule: SelfServiceSchedule = createSelfServiceScheduleFromRecurrenceModel(state.recurrence);
        const subscribeOnExtractRefresh = state.selectedSubscriptionFrequency === SubscriptionFrequency.onExtractRefresh;
        return options.action(items.keys(), schedule, subscribeOnExtractRefresh);
      }
    });
    return modalInstance.result;
  }

  function executeWithLinkedTasks<T>(items: Set<RunFlowTaskInfo>, options: IChangeLinkedTasksActionOptions<T>): Promise<IResult> {
    const useDescriptionAsName = SessionServerSelectors.displayScheduleDescriptionAsName(getAppStore().getState());
    const scheduledActionType = options.scheduledActionType;
    const linkedTasks = items.filter(task => task.taskType === 'LinkedTask');
    const flowTasks = items.filter(task => task.taskType === 'FlowTask');

    let description = Messages.errorUnknown();
    try {
      const castKey = options.descriptionTranslateKey as keyof typeof Messages;
      const castValues = options.descriptionTranslateValues as any;
      description = Messages[castKey](castValues);
    } catch (e) {
      // Do nothing
    }
    const props: SchedulePickerProps = {
      scheduledActionType, useDescriptionAsName,
      getSchedules: getSchedules(scheduledActionType),
      description: description,
      hasLinkedTasks: linkedTasks.size() > 0,
    };
    const errorReportingAction = (props: SchedulePickerProps, state: SchedulePickerState) => options
      .action(linkedTasks.pluck('linkedTaskId'), flowTasks.keys(), state.schedule.id);
    const modalInstance = openReactActionDialog({
      title: Messages.changeFrequency(),
      bodyComponent: SchedulePickerActionBody,
      initialBodyProps: props,
      initialDialogState: undefined,
      actionName: Messages.changeFrequencyConfirm({ COUNT: items.size(), COUNT_LOC: L10n.localizedCount(items.size()) }),
      action: errorReportingAction,
      actionDisabled: (props, schedule) => schedule === undefined,
      dismissDialogOnActionFailed: true,
      dialogWidth: TabDialogWidth.width500px,
    });
    return modalInstance.result;
  }

  function execute<T>(
    items: Set<T>,
    options: IChangeAdminScheduleActionOptions<T>,
  ): Promise<IResult> {
    const useDescriptionAsName = SessionServerSelectors.displayScheduleDescriptionAsName(getAppStore().getState());
    const scheduledActionType = options.scheduledActionType;

    let description = Messages.errorUnknown();
    try {
      const castKey = options.descriptionTranslateKey as keyof typeof Messages;
      const castValues = options.descriptionTranslateValues as any;
      description = Messages[castKey](castValues);
    } catch (e) {
      // Do nothing
    }
    const props: SchedulePickerProps = {
      scheduledActionType, useDescriptionAsName, getSchedules: getSchedules(scheduledActionType),
      description: description,
    };
    const errorReportingAction = (props: SchedulePickerProps, state: SchedulePickerState) => options
      .action(items.keys(), state.schedule.id)
      .then(result =>
        ActionHelpers.displayStandardToastsForResult(
          items,
          result,
          options.notificationTranslationFns,
          (errorItem: IErrorItem<T>) => options.errorDetailFn(state.schedule)(errorItem)
        ));
    const modalInstance = openReactActionDialog({
      title: Messages.changeFrequency(),
      bodyComponent: SchedulePickerActionBody,
      initialBodyProps: props,
      initialDialogState: undefined,
      actionName: Messages.changeFrequencyConfirm({ COUNT: items.size(), COUNT_LOC: L10n.localizedCount(items.size()) }),
      action: errorReportingAction,
      actionDisabled: (props, schedule) => schedule === undefined,
      dismissDialogOnActionFailed: true,
      dialogWidth: TabDialogWidth.width500px,
    });

    return modalInstance.result;
  }

  export function getSchedules(scheduledActionType: string) {
    // Filtering happens client side so we ignore the search string
    const getSchedulesMethod = (searchText: string) => {
      let query = SchedulesService.all().withOrder(ScheduleContentType.defaultOrder);
      if (scheduledActionType) {
        query = query.addFilter(SchedulesService.filterByActionType(scheduledActionType));
      }
      return query;
    };
    return getSchedulesMethod;
  }

  function executeChangeSubscriptionsFrequency(
    subscriptions: Set<ISubscriptionInfo>,
    options: IChangeSubscriptionsFrequencyOptions<ISubscriptionInfo>,
  ): Promise<IResult> {
    const useDescriptionAsName = SessionServerSelectors.displayScheduleDescriptionAsName(getAppStore().getState());
    const scheduledActionType = options.scheduledActionType;
    const subscription = subscriptions.first();
    const getSchedulesMethod = getSchedules(scheduledActionType);

    let description = Messages.errorUnknown();
    try {
      const castKey = options.descriptionTranslateKey as keyof typeof Messages;
      const castValues = options.descriptionTranslateValues as any;
      description = Messages[castKey](castValues);
    } catch (e) {
      // Do nothing
    }
    const props: SubscriptionSchedulePickerProps = {
      scheduledActionType, useDescriptionAsName, getSchedules: getSchedulesMethod,
      description: description,
      selectedSubscriptionFrequency: subscription.refreshExtractTriggered ? SubscriptionFrequency.onExtractRefresh : SubscriptionFrequency.onSelectedSchedule
    };
    const errorReportingAction = (props: SchedulePickerProps, state: SchedulePickerState) => {
      if (state.selectedScheduleOption === SubscriptionFrequency.onExtractRefresh) {
        return Subscriptions.changeFrequency(subscriptions.map(sub => sub.id), subscription.schedule.id, true)
          .then(result => {
            if (!result.errors || !result.errors.length) {
              //no error mark it as extract refresh
              subscription.refreshExtractTriggered = true;
            }
            return ActionHelpers.displayStandardToastsForResult(
              subscriptions,
              result,
              options.notificationTranslationFns,
              (errorItem: IErrorItem<ISubscriptionInfo>) => options.errorDetailFn(state.schedule)(errorItem)
            );
          });
      } else {
        return Subscriptions.changeFrequency(subscriptions.keys(), state.schedule.id, false)
          .then(result =>
            ActionHelpers.displayStandardToastsForResult(
              subscriptions,
              result,
              options.notificationTranslationFns,
              (errorItem: IErrorItem<ISubscriptionInfo>) => options.errorDetailFn(state.schedule)(errorItem)
            ));
      }
    };
    const modalInstance = openReactActionDialog({
      title: Messages.changeFrequency(),
      bodyComponent: SubscriptionSchedulePickerActionBody,
      initialBodyProps: props,
      initialDialogState: undefined,
      actionName: Messages.changeSubscriptionsFrequencyConfirm({ COUNT: subscriptions.size(), COUNT_LOC: L10n.localizedCount(subscriptions.size()) }),
      action: errorReportingAction,
      actionDisabled: (props, state) => {
        const isOnExtractRefresh = state?.selectedScheduleOption === SubscriptionFrequency.onExtractRefresh;
        const hasDefinedSchedule = state?.schedule !== undefined;
        return !(isOnExtractRefresh || hasDefinedSchedule);
      },
      dismissDialogOnActionFailed: true,
      dialogWidth: TabDialogWidth.width500px,
    });

    return modalInstance.result;
  }

  function setScheduleErrorMessageFor(errorItem: IErrorItem<IScheduledItemInfo>, schedule: ISchedule) {
    switch (errorItem.error.code) {
      case SchedulingErrorCode.SchedulingScheduleNotFound:
        return Messages.scheduleNotFound({ SCHEDULE: schedule.name });
    }
    return null;
  }

  export function createSelfServiceScheduleFromRecurrenceModel(recurrenceModel: Recurrence): SelfServiceSchedule {

    const selfServiceSchedule: SelfServiceSchedule = {
      scheduledAction: <scheduledActionEnum>'Subscription',
      scheduleType: <scheduledTypeEnum>recurrenceModel.recurrenceType,
      scheduleDetails: recurrenceModel.recurrenceDetails as ScheduleDetails
    };

    return selfServiceSchedule;
  }

  function isMixedSchedulesTypes(subscriptions: Set<ISubscriptionInfo>): boolean {
    for (const abc of subscriptions.toArray()) {
      if (abc.schedule.definedBy !== subscriptions.first().schedule.definedBy) {
        return true;
      }
    }
    return false;
  }

  // Subscription specific logic

  export function forSubscriptions(subscriptions: Set<ISubscriptionInfo>): Promise<IResult> {
    let descriptionTranslateKey: string;
    let descriptionTranslateValues: _.Dictionary<any>;

    if (subscriptions.size() === 1) {
      const sub = subscriptions.first();
      descriptionTranslateValues = { TARGET_NAME: L10n.elideIfLong(sub.targetName) };
      if (sub.targetType === SubscriptionTargetTypes.view) {
        descriptionTranslateKey = 'changeScheduleSubscriptionViewPrompt';
      } else if (sub.targetType === SubscriptionTargetTypes.workbook) {
        descriptionTranslateKey = 'changeScheduleSubscriptionWorkbookPrompt';
      }
    } else {
      descriptionTranslateKey = 'changeScheduleSubscriptionsPrompt';
      descriptionTranslateValues = { COUNT: subscriptions.size() };
    }

    const options: IChangeScheduleActionOptions<ISubscriptionInfo> = {
      notificationTranslationFns: {
        success: standardActionSuccessMsg(Messages.changeScheduleSubscriptionsSuccess),
        error: standardActionErrorMsg(Messages.changeScheduleSubscriptionsError),
      },
      scheduledActionType: ScheduleActions.Subscription,
      descriptionTranslateKey: descriptionTranslateKey,
      descriptionTranslateValues: descriptionTranslateValues
    };

    if (isMixedSchedulesTypes(subscriptions)) {
      return mixedScheduleTypesUnsupported();
    }

    if (FeatureFlags.selfServiceSchedulesEnabled && subscriptions.first().schedule.definedBy === DefinedBy.SELF_SERVICE) {
      const SSSOptions: IChangeSelfServiceScheduleActionOptions<ISubscriptionInfo> = {
        action: (ids, schedule) => Subscriptions.setSelfServiceSchedule(ids, schedule),
        notificationTranslationFns: options.notificationTranslationFns,
        errorDetailFn: () => subscriptionErrorDetailFactorySSS(),
        scheduledActionType: options.scheduledActionType,
        descriptionTranslateKey: options.descriptionTranslateKey,
        descriptionTranslateValues: options.descriptionTranslateValues,
      };
      return executeSelfServiceSchedule(subscriptions, SSSOptions);
    } else {
      const adminOptions: IChangeAdminScheduleActionOptions<ISubscriptionInfo> = {
        action: (ids, scheduleId) => Subscriptions.setSchedule(ids, scheduleId),
        notificationTranslationFns: options.notificationTranslationFns,
        errorDetailFn: (schedule) => subscriptionErrorDetailFactory(schedule),
        scheduledActionType: options.scheduledActionType,
        descriptionTranslateKey: options.descriptionTranslateKey,
        descriptionTranslateValues: options.descriptionTranslateValues
      };
      return execute(subscriptions, adminOptions);
    }
  }

  export function changeSubscriptionsFrequency(subscriptions: Set<ISubscriptionInfo>): Promise<IResult> {
    let descriptionTranslateKey: string;
    let descriptionTranslateValues: _.Dictionary<any>;

    if (subscriptions.size() === 1) {
      const sub = subscriptions.first();
      descriptionTranslateValues = { TARGET_NAME: L10n.elideIfLong(sub.targetName) };
      if (sub.targetType === SubscriptionTargetTypes.view) {
        descriptionTranslateKey = 'changeScheduleSubscriptionViewPrompt';
      } else if (sub.targetType === SubscriptionTargetTypes.workbook) {
        descriptionTranslateKey = 'changeScheduleSubscriptionWorkbookPrompt';
      }
    } else {
      descriptionTranslateKey = 'changeScheduleSubscriptionsPrompt';
      descriptionTranslateValues = { COUNT: subscriptions.size() };
    }

    const options: IChangeScheduleActionOptions<ISubscriptionInfo> = {
      notificationTranslationFns: {
        success: standardActionSuccessMsg(Messages.changeSubscriptionsFrequencySuccess),
        error: standardActionErrorMsg(Messages.changeSubscriptionsFrequencyFailure),
      },
      scheduledActionType: ScheduleActions.Subscription,
      descriptionTranslateKey: descriptionTranslateKey,
      descriptionTranslateValues: descriptionTranslateValues
    };

    if (isMixedSchedulesTypes(subscriptions)) {
      return mixedScheduleTypesUnsupported();
    }

    if (FeatureFlags.selfServiceSchedulesEnabled && subscriptions.first().schedule.definedBy === DefinedBy.SELF_SERVICE) {
      const frequencyOption = subscriptions.first().refreshExtractTriggered
        ? SubscriptionFrequency.onExtractRefresh
        : SubscriptionFrequency.onSelectedSchedule;
      const SSSOptions: IChangeSelfServiceSubscriptionsFrequencyOptions<ISubscriptionInfo> = {
        action: (ids, schedule, subscribeOnExtractRefresh) => Subscriptions.changeSelfServiceSubscriptionsFrequency(ids, schedule, subscribeOnExtractRefresh),
        notificationTranslationFns: options.notificationTranslationFns,
        errorDetailFn: () => subscriptionErrorDetailFactorySSS(),
        scheduledActionType: options.scheduledActionType,
        descriptionTranslateKey: options.descriptionTranslateKey,
        descriptionTranslateValues: options.descriptionTranslateValues,
        frequncyOption: frequencyOption,
      };
      // TODO: This is where the other version of the SSS dialog needs to be used
      return executeSelfServiceSchedule(subscriptions, SSSOptions);
    } else {
      const adminOptions: IChangeSubscriptionsFrequencyOptions<ISubscriptionInfo> = {
        action: (ids, scheduleId, subscribeOnExtractRefresh) => Subscriptions.changeFrequency(ids, scheduleId, subscribeOnExtractRefresh),
        notificationTranslationFns: options.notificationTranslationFns,
        errorDetailFn: (schedule) => subscriptionErrorDetailFactory(schedule),
        scheduledActionType: options.scheduledActionType,
        descriptionTranslateKey: options.descriptionTranslateKey,
        descriptionTranslateValues: options.descriptionTranslateValues
      };
      return executeChangeSubscriptionsFrequency(subscriptions, adminOptions);
    }
  }

  function subscriptionErrorDetailFactory(schedule: ISchedule) {
    return (errorItem: IErrorItem<ISubscriptionInfo>): IErrorDetail => {
      return {
        itemName: errorItem.item.subject,
        errorMessage: subscriptionErrorMessageFor(errorItem, schedule)
      };
    };
  }

  function subscriptionErrorDetailFactorySSS() {
    return (errorItem: IErrorItem<ISubscriptionInfo>): IErrorDetail => {
      return {
        itemName: errorItem.item.subject,
        errorMessage: Messages.changeSubscriptionScheduleFailure()
      };
    };
  }

  function subscriptionErrorMessageFor(errorItem: IErrorItem<ISubscriptionInfo>, schedule: ISchedule): string {
    switch (errorItem.error.code) {
      case CommonErrorCode.SubscriptionNotFound:
        return Messages.subscriptionNotFound({ SUBSCRIPTION_SUBJECT: errorItem.item.subject });
      case SubscriptionErrorCode.SubscriptionFrequencyChangeToERSErrorCode:
        return Messages.subscriptionChangeToERSFailed();
    }
    return setScheduleErrorMessageFor(errorItem, schedule);
  }

  // Extract Tasks specific logic

  export function forExtractTasks(extractTasks: Set<IExtractTaskInfo>): Promise<IResult> {
    let descriptionTranslateKey: string;
    let descriptionTranslateValues: _.Dictionary<any>;

    if (extractTasks.size() === 1) {
      const task = extractTasks.first();
      if (task.workbook) {
        descriptionTranslateKey = 'changeScheduleExtractTasksWorkbookPrompt';
        descriptionTranslateValues = { PARENT_NAME: L10n.elideIfLong(task.workbook.name) };
      } else if (task.datasource) {
        descriptionTranslateKey = 'changeScheduleExtractTasksDatasourcePrompt';
        descriptionTranslateValues = { PARENT_NAME: L10n.elideIfLong(task.datasource.name) };
      }
    } else {
      descriptionTranslateKey = 'changeScheduleExtractTasksPrompt';
      descriptionTranslateValues = { COUNT: extractTasks.size() };
    }

    const options: IChangeAdminScheduleActionOptions<IExtractTaskInfo> = {
      action: (ids, scheduleId) => ExtractTasks.setSchedule(ids, scheduleId),
      notificationTranslationFns: {
        success: standardActionSuccessMsg(Messages.changeScheduleExtractTasksSuccess),
        error: standardActionErrorMsg(Messages.changeScheduleExtractTasksError),
      },
      errorDetailFn: (schedule) => extractTaskErrorDetailFactory(schedule),
      scheduledActionType: ScheduleActions.Extract,
      descriptionTranslateKey: descriptionTranslateKey,
      descriptionTranslateValues: descriptionTranslateValues
    };

    return execute(extractTasks, options);
  }

  function extractTaskErrorDetailFactory(schedule: ISchedule) {
    return (errorItem: IErrorItem<IExtractTaskInfo>): IErrorDetail => {
      const namedItem: INamedItemInfo = errorItem.item.datasource || errorItem.item.workbook;

      return {
        itemName: namedItem.name,
        errorMessage: extractTaskErrorMessageFor(errorItem, schedule)
      };
    };
  }

  function extractTaskErrorMessageFor(errorItem: IErrorItem<IExtractTaskInfo>, schedule: ISchedule): string {
    switch (errorItem.error.code) {
      case SchedulingErrorCode.SchedulingTaskNotFound:
        if (errorItem.item.workbook)
          return Messages.extractTaskNotFoundForWorkbook({ WORKBOOK_NAME: L10n.elideIfLong(errorItem.item.workbook.name) });
        if (errorItem.item.datasource)
          return Messages.extractTaskNotFoundForDatasource({ DATASOURCE_NAME: L10n.elideIfLong(errorItem.item.datasource.name) });
    }
    return setScheduleErrorMessageFor(errorItem, schedule);
  }

  // Flow Tasks specific logic
  export function forRunFlowTasks(runFlowTaskInfos: Set<RunFlowTaskInfo>): Promise<IResult> {
    const linkedTasks = runFlowTaskInfos.filter(task => task.taskType === 'LinkedTask');
    const flowTasks = runFlowTaskInfos.filter(task => task.taskType === 'FlowTask');

    let descriptionTranslateKey: string;
    let descriptionTranslateValues: _.Dictionary<any>;

    if (runFlowTaskInfos.size() === 1) {
      const task = runFlowTaskInfos.first();
      if (task.taskType === 'LinkedTask') {
        descriptionTranslateKey = 'changeScheduleRunLinkedTaskPrompt';
      } else {
        descriptionTranslateKey = 'changeScheduleRunFlowTaskPrompt';
        descriptionTranslateValues = { FLOW_NAME: L10n.elideIfLong(task.flow.name) };
      }
    } else {
      descriptionTranslateKey = 'changeScheduleRunFlowTasksPrompt';
      descriptionTranslateValues = { COUNT_LOC: runFlowTaskInfos.size() };
    }

    const flowTaskNotificationTranslationFns = {
      success: standardActionSuccessMsg(Messages.changeScheduleRunFlowTasksSuccess),
      error: standardActionErrorMsg(Messages.changeScheduleRunFlowTasksError),
    };

    if (areLinkedTasksEnabled() && linkedTasks.size() > 0) {
      const linkedTaskNotificationTranslationFns = {
        success: standardActionSuccessMsg(Messages.changeScheduleRunLinkedTasksSuccess),
        error: standardActionErrorMsg(Messages.changeScheduleRunLinkedTasksError),
      };
      const options: IChangeLinkedTasksActionOptions<RunFlowTaskInfo> = {
        action: (linkedTaskIds, flowTaskIds, scheduleId) => LinkedTasks.updateLinkedTasksSchedule(linkedTaskIds, scheduleId).then((result) => {
          ActionHelpers.displayStandardToastsForResult(
            linkedTasks,
            result,
            linkedTaskNotificationTranslationFns,
            (errorItem: IErrorItem<RunFlowTaskInfo>) => errorDetailForFlowTask(errorItem)
          );
          return result;
        }) && (flowTaskIds.length > 0 && RunFlowTasks.setSchedule(flowTaskIds, scheduleId)).then((result) => {
          ActionHelpers.displayStandardToastsForResult(
            flowTasks,
            result,
            flowTaskNotificationTranslationFns,
            (errorItem: IErrorItem<RunFlowTaskInfo>) => errorDetailForFlowTask(errorItem)
          );
          return result;
        }),
        notificationTranslationFns: linkedTaskNotificationTranslationFns,
        errorDetailFn: (schedule) => runFlowTaskErrorDetailFactory(schedule),
        scheduledActionType: ScheduleActions.Flow,
        descriptionTranslateKey: descriptionTranslateKey,
        descriptionTranslateValues: descriptionTranslateValues,
      };
      return executeWithLinkedTasks(runFlowTaskInfos, options);
    }

    const options: IChangeAdminScheduleActionOptions<RunFlowTaskInfo> = {
      action: (ids, scheduleId) => RunFlowTasks.setSchedule(ids, scheduleId),
      notificationTranslationFns: flowTaskNotificationTranslationFns,
      errorDetailFn: (schedule) => runFlowTaskErrorDetailFactory(schedule),
      scheduledActionType: ScheduleActions.Flow,
      descriptionTranslateKey: descriptionTranslateKey,
      descriptionTranslateValues: descriptionTranslateValues
    };

    return execute(runFlowTaskInfos, options);
  }

  function errorDetailForFlowTask(errorItem: IErrorItem<RunFlowTaskInfo>): IErrorDetail {
    const itemName = errorItem.item ? errorItem.item.schedule.name : '';
    let errorMessage: string;

    switch (errorItem.error.code) {
      case CommonErrorCode.CallingUserInsufficientPermissions:
        errorMessage = Messages.runFlowTasksInsufficientPermissions();
        break;
      default:
        errorMessage = Messages.errorUnknown();
    }

    return { itemName, errorMessage };
  }

  function runFlowTaskErrorDetailFactory(schedule: ISchedule) {
    return (errorItem: IErrorItem<RunFlowTaskInfo>): IErrorDetail => {
      const namedItem: INamedItemInfo = errorItem.item.flow;
      return {
        itemName: namedItem.name,
        errorMessage: runFlowTaskErrorMessageFor(errorItem, schedule)
      };
    };
  }

  function runFlowTaskErrorMessageFor(errorItem: IErrorItem<RunFlowTaskInfo>, schedule: ISchedule): string {
    switch (errorItem.error.code) {
      case SchedulingErrorCode.SchedulingTaskNotFound:
        return Messages.runFlowTaskNotFoundForFlow({ FLOW_NAME: L10n.elideIfLong(errorItem.item.flow.name) });
    }
    return setScheduleErrorMessageFor(errorItem, schedule);
  }
}
